/*
 * ██████╗ ██╗████████╗███████╗ █████╗  ██████╗████████╗ ██████╗ ██████╗ ██╗   ██╗
 * ██╔══██╗██║╚══██╔══╝██╔════╝██╔══██╗██╔════╝╚══██╔══╝██╔═══██╗██╔══██╗╚██╗ ██╔╝
 * ██████╔╝██║   ██║   █████╗  ███████║██║        ██║   ██║   ██║██████╔╝ ╚████╔╝
 * ██╔══██╗██║   ██║   ██╔══╝  ██╔══██║██║        ██║   ██║   ██║██╔══██╗  ╚██╔╝
 * ██████╔╝██║   ██║   ██║     ██║  ██║╚██████╗   ██║   ╚██████╔╝██║  ██║   ██║
 * ╚═════╝ ╚═╝   ╚═╝   ╚═╝     ╚═╝  ╚═╝ ╚═════╝   ╚═╝    ╚═════╝ ╚═╝  ╚═╝   ╚═╝
 *
 *
 * Copyright (c) Bitfactory GmbH. All rights reserved.
 * https://www.bitfactory.io
 *
 * Redistribution and use in source and binary forms, with or without
 * modification, are not permitted.
 *
 * THIS SOFTWARE IS PROVIDED BY THE COPYRIGHT HOLDERS AND CONTRIBUTORS
 * "AS IS" AND ANY EXPRESS OR IMPLIED WARRANTIES, INCLUDING, BUT NOT
 * LIMITED TO, THE IMPLIED WARRANTIES OF MERCHANTABILITY AND FITNESS
 * FOR A PARTICULAR PURPOSE ARE DISCLAIMED. IN NO EVENT SHALL THE
 * COPYRIGHT OWNER OR CONTRIBUTORS BE LIABLE FOR ANY DIRECT, INDIRECT,
 * INCIDENTAL, SPECIAL, EXEMPLARY, OR CONSEQUENTIAL DAMAGES INCLUDING,
 * BUT NOT LIMITED TO, PROCUREMENT OF SUBSTITUTE GOODS OR SERVICES;
 * LOSS OF USE, DATA, OR PROFITS; OR BUSINESS INTERRUPTION) HOWEVER
 * CAUSED AND ON ANY THEORY OF LIABILITY, WHETHER IN CONTRACT, STRICT
 * LIABILITY, OR TORT (INCLUDING NEGLIGENCE OR OTHERWISE) ARISING IN
 * ANY WAY OUT OF THE USE OF THIS SOFTWARE, EVEN IF ADVISED OF THE
 * POSSIBILITY OF SUCH DAMAGE.
 */

import React from 'react';
import { observer } from 'mobx-react';
import { Box, Typography } from '@material-ui/core';
import { OptionGroup } from '../../../types/elements/Element';
import { ChoiceViewUseStyles } from './Choice/ChoiceViewStyles';
import { ELEMENTS_TYPE } from '../../../utils/elementFactory';

interface Props {
	/**
	 * Array of option groups to render. Each group can display checkboxes (multiple choice)
	 * or radio buttons (single choice) based on the multiple_choice flag.
	 */
	optionsGroups: OptionGroup[];
	/**
	 * Selected options object mapping group titles or IDs to their selected values.
	 * Format examples:
	 * - By title: { "Test": ["Option 1"] } for multiple choice
	 * - By title: { "Test": "Option 1" } for single choice
	 * - By ID: { "12": ["Option 1"] } for multiple choice
	 * - By ID: { "12": "Option 1" } for single choice
	 * Title-based lookup is prioritized over ID-based lookup.
	 */
	selectedOptionsObject?: Record<string, string[] | string>;
	/**
	 * Element type to determine read-only vs interactive rendering.
	 */
	type: ELEMENTS_TYPE;
}

/**
 * Component that renders create-entry option groups as interactive elements with checkboxes or radio buttons.
 * Supports both single-choice (radio buttons) and multiple-choice (checkboxes) options.
 * Each group displays a title and description along with its options.
 *
 * @author Julien Tietke <j.tietke@tj-labs.de>
 */
export const CreateEntryOptionsView = observer((props: Props) => {
	const { optionsGroups, selectedOptionsObject, type } = props;
	const classes = ChoiceViewUseStyles();

	/**
	 * Checks if a specific option is selected within a group.
	 * Handles both single-choice (direct comparison) and multiple-choice (array inclusion).
	 * Supports lookup by both group ID and group title.
	 */
	const isOptionSelected = (groupId: number, groupTitle: string, optionText: string): boolean => {
		if (!selectedOptionsObject) return false;

		// Try to get selected value by title first (more common format)
		let selectedValue = selectedOptionsObject[groupTitle];

		// Fall back to group ID if not found by title
		if (!selectedValue) {
			selectedValue = selectedOptionsObject[groupId.toString()];
		}

		if (!selectedValue) return false;

		if (Array.isArray(selectedValue)) {
			// Multiple choice - check if option is in array
			return selectedValue.includes(optionText);
		} else {
			// Single choice - direct comparison
			return selectedValue === optionText;
		}
	};

	/**
	 * Renders the radio button control (for single-choice options).
	 */
	const renderRadioButton = (isSelected: boolean) => (
		<>
			{isSelected ? (
				<Box className={`${classes.textSelectBox} ${classes.radioChecked}`}>
					<Box className={classes.radioCheckedInside} />
				</Box>
			) : (
				<Box className={`${classes.textSelectBox} ${classes.radioUnchecked}`} />
			)}
		</>
	);

	/**
	 * Renders the checkbox control (for multiple-choice options).
	 */
	const renderCheckbox = (isSelected: boolean) => (
		<img
			className={`${classes.textSelectBox} ${classes.checkbox}`}
			src={isSelected ? '/img/checkbox-checked.png' : '/img/checkbox-unchecked.png'}
			alt={isSelected ? 'Checked Checkbox' : 'Unchecked Checkbox'}
		/>
	);

	return (
		<Box>
			{optionsGroups.map((optionGroup: OptionGroup) => (
				<Box key={`optionGroup-${optionGroup.id}`} className={classes.container}>
					<Typography variant='h3' style={{ marginBottom: '8px', fontWeight: 'bold' }}>
						{optionGroup.title}
					</Typography>
					{optionGroup.description && (
						<Typography variant='body2' style={{ marginBottom: '12px', color: '#666' }}>
							{optionGroup.description}
						</Typography>
					)}
					<Box style={{ marginLeft: '16px' }}>
						{optionGroup.options.map((optionText: string, index: number) => {
							const isSelected =
								type === ELEMENTS_TYPE.ENTRY ? isOptionSelected(optionGroup.id, optionGroup.title, optionText) : false;
							const controlKey = `option-${optionGroup.id}-${index}`;

							return (
								<Box className={classes.box} key={controlKey}>
									{optionGroup.multiple_choice ? renderCheckbox(isSelected) : renderRadioButton(isSelected)}
									<Typography className={classes.text}>{optionText}</Typography>
								</Box>
							);
						})}
					</Box>
				</Box>
			))}
		</Box>
	);
});
